import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowLeft, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

const PACKAGES = {
  postcard: { name: 'Postcard', price: 10, description: 'Eine digitale Nachricht mit QR-Code', features: ['1 Nachricht', 'QR-Code Download', 'Lebenslanger Zugriff'] },
  start: { name: 'Start', price: 39, description: 'Für eine Person mit 1 Empfänger', features: ['1 Empfänger', 'Wenig Content', 'Selbst bedienen'] },
  family: { name: 'Family', price: 99, description: 'Für Familien: bis 2 Empfänger, bis 5 Events', features: ['bis 2 Empfänger', 'bis 5 Events', 'Mehr Inhalte'] },
  familyplus: { name: 'Family+', price: 999, description: 'Ganze Familie: unbegrenzte Empfänger & Events', features: ['Ganze Familie', 'Unbegrenzte Events', 'Mehr Speicher'] },
  support: { name: 'Family+ Support', price: 2999, description: 'Mit Bankschließfach und persönlicher Beratung', features: ['Bankschließfach', 'Persönliche Beratung', 'Gold-Verwaltung'] }
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_dummy');

export default function Checkout() {
  const navigate = useNavigate();
  const { packageId } = useParams();
  const [selectedPackage, setSelectedPackage] = useState(packageId || 'postcard');
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistData, setWaitlistData] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherMessage, setVoucherMessage] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'DE'
  });

  const pkg = PACKAGES[selectedPackage];
  const basePrice = pkg.price;
  const discountValue = voucher ? voucher.discount_value : 0;
  const subtotal = (basePrice * 1.19).toFixed(2);
  const total = ((basePrice - discountValue) * 1.19).toFixed(2);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const applyVoucher = async () => {
    if (!voucherCode.trim()) {
      setVoucherMessage('❌ Bitte gib einen Gutschein-Code ein');
      return;
    }

    setVoucherLoading(true);
    setVoucherMessage('');
    
    try {
      const res = await axios.post('http://localhost:4500/api/checkout/apply-voucher', {
        code: voucherCode.trim(),
        basePrice
      });

      if (res.data.success) {
        setVoucher(res.data);
        setVoucherMessage(`✅ Gutschein ${res.data.code} angewendet! ${res.data.discount_value.toFixed(2)}€ Rabatt`);
        setVoucherCode('');
      }
    } catch (error) {
      setVoucherMessage('❌ ' + (error.response?.data?.error || 'Gutschein ungültig oder abgelaufen'));
      setVoucher(null);
    } finally {
      setVoucherLoading(false);
    }
  };

  const handleContinue = () => {
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.address) {
      alert('Bitte fülle alle erforderlichen Felder aus');
      return;
    }
    setStep(2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/#pricing')}
          className="flex items-center gap-2 text-purple-300 hover:text-purple-200 mb-8 transition"
        >
          <ArrowLeft size={20} />
          Zurück
        </button>

        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold mb-8"
        >
          Checkout
        </motion.h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Customer Info */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
              >
                {/* Customer Info */}
                <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/30">
                  <h2 className="text-2xl font-bold mb-4">Deine Daten</h2>
                  <div className="space-y-4">
                    <input
                      type="email"
                      name="email"
                      placeholder="E-Mail-Adresse"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 transition"
                      required
                    />
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="firstName"
                        placeholder="Vorname"
                        value={formData.firstName}
                        onChange={handleFormChange}
                        className="px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 transition"
                        required
                      />
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Nachname"
                        value={formData.lastName}
                        onChange={handleFormChange}
                        className="px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 transition"
                        required
                      />
                    </div>

                    <input
                      type="text"
                      name="address"
                      placeholder="Straße und Hausnummer"
                      value={formData.address}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 transition"
                      required
                    />

                    <div className="grid md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="postalCode"
                        placeholder="Postleitzahl"
                        value={formData.postalCode}
                        onChange={handleFormChange}
                        className="px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 transition"
                      />
                      <input
                        type="text"
                        name="city"
                        placeholder="Stadt"
                        value={formData.city}
                        onChange={handleFormChange}
                        className="px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 transition"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleContinue}
                    className="w-full mt-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 rounded-lg transition-all"
                  >
                    Zahlungsmethode wählen →
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Payment Method & Package Selection */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
              >
                {/* Package Selector */}
                <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/30">
                  <h2 className="text-2xl font-bold mb-4">📦 Paket wählen</h2>
                  <p className="text-slate-400 text-sm mb-4">Du kannst dein Paket hier noch wechseln, falls du mehr Funktionen möchtest</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(PACKAGES).map(([key, package_item]) => (
                      <button
                        key={key}
                        onClick={() => {
                          setSelectedPackage(key);
                        }}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          selectedPackage === key
                            ? 'border-purple-500 bg-purple-600/20'
                            : 'border-slate-700 hover:border-slate-600 bg-slate-700/30'
                        }`}
                      >
                        <div className="font-bold text-sm">{package_item.name}</div>
                        <div className="text-xs text-slate-400">€{package_item.price}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/30">
                  <h2 className="text-2xl font-bold mb-4">Zahlungsart</h2>
                  
                  <div className="space-y-3">
                    {/* Stripe */}
                    <button
                      onClick={() => setPaymentMethod('stripe')}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left flex items-center gap-4 ${
                        paymentMethod === 'stripe'
                          ? 'border-purple-500 bg-purple-600/20'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center" style={{ borderColor: paymentMethod === 'stripe' ? '#a855f7' : '#64748b' }}>
                        {paymentMethod === 'stripe' && <div className="w-3 h-3 rounded-full bg-purple-500" />}
                      </div>
                      <div>
                        <div className="font-bold">Kreditkarte</div>
                        <div className="text-sm text-slate-400">Visa, Mastercard, American Express</div>
                      </div>
                    </button>

                    {/* PayPal */}
                    <button
                      onClick={() => setPaymentMethod('paypal')}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left flex items-center gap-4 ${
                        paymentMethod === 'paypal'
                          ? 'border-purple-500 bg-purple-600/20'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center" style={{ borderColor: paymentMethod === 'paypal' ? '#a855f7' : '#64748b' }}>
                        {paymentMethod === 'paypal' && <div className="w-3 h-3 rounded-full bg-purple-500" />}
                      </div>
                      <div>
                        <div className="font-bold">PayPal</div>
                        <div className="text-sm text-slate-400">Sicher mit deinem PayPal-Konto</div>
                      </div>
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <button
                      onClick={() => setStep(1)}
                      className="px-6 py-3 border-2 border-slate-600 rounded-lg hover:border-slate-500 transition"
                    >
                      ← Zurück
                    </button>
                    <button
                      onClick={() => setStep(3)}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 rounded-lg transition-all"
                    >
                      Fortfahren →
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Payment Details */}
            {step === 3 && (
              <Elements stripe={stripePromise}>
                <PaymentStep
                  packageId={selectedPackage}
                  pkg={pkg}
                  paymentMethod={paymentMethod}
                  formData={formData}
                  voucher={voucher}
                  basePrice={basePrice}
                  total={total}
                  onBack={() => setStep(2)}
                  onWaitlist={(data) => {
                    setWaitlistData(data);
                    setShowWaitlist(true);
                  }}
                />
              </Elements>
            )}
          </div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/30 sticky top-8">
              <h3 className="text-xl font-bold mb-6">Zusammenfassung</h3>
              
              <div className="space-y-3 mb-6 pb-6 border-b border-slate-700">
                <div className="text-lg font-bold">{pkg.name}</div>
                <div className="text-sm text-slate-400">{pkg.description}</div>
                
                <div className="space-y-2 mt-4">
                  {pkg.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check size={16} className="text-green-400" />
                      {feature}
                    </div>
                  ))}
                </div>

                {!voucher && (
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <div className="text-sm font-bold mb-3">🎁 Hast du einen Gutschein?</div>
                    <div className="flex gap-2 w-full">
                      <input
                        type="text"
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value)}
                        placeholder="Gutschein-Code eingeben"
                        className="flex-1 px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-purple-500 transition text-sm text-white placeholder-slate-400 min-w-0"
                        onKeyPress={(e) => e.key === 'Enter' && applyVoucher()}
                      />
                      <button
                        onClick={applyVoucher}
                        disabled={voucherLoading || !voucherCode.trim()}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition disabled:opacity-50 text-sm font-bold whitespace-nowrap flex-shrink-0"
                      >
                        {voucherLoading ? 'Prüfe...' : 'Anwenden'}
                      </button>
                    </div>
                    {voucherMessage && (
                      <div className="text-xs mt-2 text-slate-400">
                        {voucherMessage}
                      </div>
                    )}
                  </div>
                )}

                {voucher && (
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-3">
                      <div className="text-sm font-bold text-green-300">✅ Gutschein aktiv</div>
                      <div className="text-xs text-slate-300 mt-1">{voucher.code}</div>
                      <button
                        onClick={() => {
                          setVoucher(null);
                          setVoucherMessage('');
                        }}
                        className="text-xs text-slate-400 hover:text-slate-200 mt-2 underline"
                      >
                        Gutschein entfernen
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2 mb-6">
                <div className="flex justify-between text-slate-400">
                  <span>Subtotal</span>
                  <span>€{pkg.price.toFixed(2)}</span>
                </div>
                {voucher && (
                  <div className="flex justify-between text-green-400 text-sm">
                    <span>Rabatt ({voucher.discount_type === 'percent' ? voucher.discount_percent + '%' : 'Pauschal'})</span>
                    <span>-€{voucher.discount_value.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-400 text-sm">
                  <span>MwSt. (19%)</span>
                  <span>€{((basePrice - discountValue) * 0.19).toFixed(2)}</span>
                </div>
              </div>

              <div className="border-t border-slate-700 pt-4 flex justify-between text-xl font-bold">
                <span>Gesamt</span>
                <span>€{total}</span>
              </div>

              <div className="mt-6 p-4 bg-slate-700/50 rounded-lg text-sm text-slate-300">
                💳 Mit Verschlüsselung gesichert. Deine Daten sind sicher.
              </div>
            </div>
          </motion.div>
        </div>

        {/* Waitlist Modal */}
        {showWaitlist && (
          <WaitlistModal
            data={waitlistData}
            onClose={() => setShowWaitlist(false)}
            onNavigate={() => {
              navigate('/');
              setShowWaitlist(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

function PaymentStep({ packageId, pkg, paymentMethod, formData, voucher, basePrice, total, onBack, onWaitlist }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  if (paymentMethod === 'stripe') {
    return (
      <StripePaymentForm
        stripe={stripe}
        elements={elements}
        packageId={packageId}
        pkg={pkg}
        formData={formData}
        voucher={voucher}
        basePrice={basePrice}
        total={total}
        loading={loading}
        setLoading={setLoading}
        onBack={onBack}
        onWaitlist={onWaitlist}
      />
    );
  }

  if (paymentMethod === 'paypal') {
    return (
      <PayPalPaymentForm
        packageId={packageId}
        pkg={pkg}
        formData={formData}
        voucher={voucher}
        basePrice={basePrice}
        total={total}
        loading={loading}
        setLoading={setLoading}
        onBack={onBack}
        onWaitlist={onWaitlist}
      />
    );
  }
}

function StripePaymentForm({ stripe, elements, packageId, pkg, formData, voucher, basePrice, total, loading, setLoading, onBack, onWaitlist }) {
  const handlePaymentClick = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    try {
      // Send to backend - creates pending order + sends emails
      const res = await axios.post('http://localhost:4500/api/payments/pending', {
        packageId,
        amount: basePrice,
        voucherCode: voucher?.code || null,
        voucherDiscount: voucher?.discount_value || 0,
        finalAmount: basePrice - (voucher?.discount_value || 0),
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        paymentMethod: 'stripe'
      });

      onWaitlist({
        email: formData.email,
        firstName: formData.firstName,
        package: pkg.name,
        voucherCode: res.data.voucherCode
      });
    } catch (error) {
      alert('Fehler: ' + error.message);
      setLoading(false);
    }
  }

  return (
    <>
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/30">
          <h2 className="text-2xl font-bold mb-4">Zahlungsdaten</h2>
          
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-sm font-bold mb-2">Kartennummer</label>
              <div className="p-4 bg-slate-700/50 border border-slate-600 rounded-lg">
                <CardNumberElement
                  options={{
                    style: {
                      base: { color: '#e2e8f0', fontSize: '16px' },
                      invalid: { color: '#ef4444' }
                    }
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">Ablaufdatum (MM/YY)</label>
              <div className="p-4 bg-slate-700/50 border border-slate-600 rounded-lg">
                <CardExpiryElement
                  options={{
                    style: {
                      base: { color: '#e2e8f0', fontSize: '16px' },
                      invalid: { color: '#ef4444' }
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-400 mb-4">💡 Nur diese Felder erforderlich. Prüfziffer nicht nötig.</p>

          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-3 border-2 border-slate-600 rounded-lg hover:border-slate-500 transition"
            >
              ← Zurück
            </button>
            <button
              type="button"
              onClick={handlePaymentClick}
              disabled={loading || !stripe}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50"
            >
              {loading ? 'Wird verarbeitet...' : `€${total} zahlen`}
            </button>
          </div>
        </div>
      </motion.form>
    </>
  );
}

function PayPalPaymentForm({ packageId, pkg, formData, voucher, basePrice, total, loading, setLoading, onBack, onWaitlist }) {
  const handlePayPalPayment = async () => {
    setLoading(true);
    try {
      const res = await axios.post('http://localhost:4500/api/payments/pending', {
        packageId,
        amount: basePrice,
        voucherCode: voucher?.code || null,
        voucherDiscount: voucher?.discount_value || 0,
        finalAmount: basePrice - (voucher?.discount_value || 0),
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        paymentMethod: 'paypal'
      });

      onWaitlist({
        email: formData.email,
        firstName: formData.firstName,
        package: pkg.name,
        voucherCode: res.data.voucherCode
      });
    } catch (error) {
      alert('Fehler: ' + error.message);
      setLoading(false);
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/30">
          <h2 className="text-2xl font-bold mb-4">PayPal-Bestätigung</h2>
          
          <div className="bg-slate-700/50 p-6 rounded-lg mb-6">
            <div className="text-lg font-bold mb-2">Zahlungsbestätigung</div>
            <div className="text-slate-300 mb-4">
              Bestätige deine Zahlung via PayPal. Du wirst anschließend weitergeleitet.
            </div>
            <div className="space-y-2 text-sm text-slate-400">
              <div>📧 {formData.email}</div>
              <div>💳 Gesamtbetrag: €{total}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={onBack}
              className="px-6 py-3 border-2 border-slate-600 rounded-lg hover:border-slate-500 transition"
            >
              ← Zurück
            </button>
            <button
              onClick={handlePayPalPayment}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50"
            >
              {loading ? 'Wird verarbeitet...' : '✓ Zahlung bestätigen'}
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

function WaitlistModal({ data, onClose, onNavigate }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-gradient-to-br from-purple-900 to-slate-900 rounded-lg p-8 max-w-md border border-purple-500/50"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-white">🎉 Fast geschafft!</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4 text-slate-200 mb-6">
          <p className="text-base">
            Danke für dein Interesse an <span className="font-bold">{data.package}</span>! 💜
          </p>
          
          <div className="bg-slate-800/50 p-4 rounded-lg border border-yellow-500/30 mb-4">
            <p className="text-sm mb-2">
              Aufgrund der hohen Nachfrage überarbeiten wir momentan unsere Infrastruktur. Wir nehmen derzeit keine neuen User auf.
            </p>
          </div>

          <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-3 mb-4">
            <p className="text-xs text-amber-200 leading-relaxed">
              <strong>Dein Konto wird NICHT belastet!</strong> Keine Abbuchung findet statt. Deine Zahlungsdaten werden weder gespeichert noch verwendet.
            </p>
          </div>

          <div className="bg-gradient-to-r from-green-900 to-emerald-900 p-4 rounded-lg border border-green-500/50">
            <p className="text-sm font-bold mb-2">🎁 50% Rabatt-Gutschein</p>
            <p className="text-xs text-slate-300 mb-3">
              Du erhältst per E-Mail einen exklusiven Gutschein mit 50% Rabatt:
            </p>
            <div className="bg-slate-900 p-3 rounded font-mono text-sm text-green-400 break-all">
              {data.voucherCode}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              ✓ Dieser Gutschein ist einmalig pro Paket anwendbar
            </p>
          </div>

          <div className="bg-slate-800/50 p-4 rounded-lg">
            <p className="text-sm mb-2">📬 Benachrichtigungen</p>
            <p className="text-xs text-slate-400">
              Du erhältst eine E-Mail an <span className="font-bold text-slate-200">{data.email}</span> sobald:
            </p>
            <ul className="text-xs text-slate-400 mt-2 space-y-1 ml-2">
              <li>✓ Unser Gutschein aktiv ist</li>
              <li>✓ Neue Kontingente verfügbar sind</li>
              <li>✓ Du dein Paket aktivieren kannst</li>
            </ul>
          </div>
        </div>

        <button
          onClick={onNavigate}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 rounded-lg transition-all"
        >
          Startseite
        </button>
      </motion.div>
    </motion.div>
  );
}
